import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  MenuItem,
  LinearProgress,
  Tooltip,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Security as AdminIcon,
  ManageAccounts as DeanIcon,
  Badge as StaffIcon
} from '@mui/icons-material';
import api from '../services/api';

const SystemUserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'ADMIN',
    email: '',
    prefix: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    is_active: 1
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/system-users');
      setUsers(response.data);
    } catch (err) {
      console.error(err);
      showSnackbar('ไม่สามารถโหลดข้อมูลผู้ใช้งานได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpen = (user: any = null) => {
    if (user) {
      setEditingId(user.user_id);
      setFormData({
        username: user.username || '',
        password: '',
        role: user.role || 'ADMIN',
        email: user.email || '',
        prefix: user.prefix || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
        is_active: user.is_active === 0 ? 0 : 1
      });
    } else {
      setEditingId(null);
      setFormData({ 
        username: '', 
        password: '', 
        role: 'ADMIN', 
        email: '', 
        prefix: '', 
        first_name: '', 
        last_name: '', 
        phone_number: '', 
        is_active: 1 
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked ? 1 : 0 });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async () => {
    if (!formData.username || !formData.first_name || !formData.last_name || !formData.email) {
      showSnackbar('กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน', 'error');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/system-users/${editingId}`, formData);
        showSnackbar('อัปเดตข้อมูลสำเร็จ');
      } else {
        await api.post('/system-users', formData);
        showSnackbar('เพิ่มผู้ใช้งานสำเร็จ');
      }
      handleClose();
      fetchUsers();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบผู้ใช้งานระบบ? การดำเนินการนี้ไม่สามารถย้อนกลับได้')) {
      setLoading(true);
      try {
        await api.delete(`/system-users/${id}`);
        showSnackbar('ลบผู้ใช้งานสำเร็จ');
        fetchUsers();
      } catch (err: any) {
        showSnackbar(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบข้อมูล', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {(loading || submitting) && <LinearProgress sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 2000 }} />}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            จัดการผู้ใช้งานระบบ (Admin / Dean / Staff / Research Staff)
          </Typography>
          <Typography variant="body2" color="textSecondary">
            จัดการบัญชีสำหรับ ผู้ดูแลระบบ (Admin), ผู้บริหาร (Dean), เจ้าหน้าที่ฝ่ายฝึกฯ (Staff) และเจ้าหน้าที่ฝ่ายวิจัย (Research Staff)
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          เพิ่มผู้ใช้งานใหม่
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'primary.light' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ชื่อ-นามสกุล</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Username</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>อีเมล</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>บทบาท</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>สถานะ</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u: any) => (
              <TableRow key={u.user_id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {u.prefix || ''}{u.first_name || '(ยังไม่ตั้งชื่อ)'} {u.last_name || ''}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">{u.phone_number || '-'}</Typography>
                </TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    icon={u.role === 'ADMIN' ? <AdminIcon fontSize="small" /> : (u.role === 'DEAN' ? <DeanIcon fontSize="small" /> : <StaffIcon fontSize="small" />)}
                    label={u.role === 'RESEARCH_STAFF' ? 'RESEARCH_STAFF (เจ้าหน้าที่ฝ่ายวิจัย)' : u.role} 
                    color={u.role === 'ADMIN' ? 'primary' : (u.role === 'DEAN' ? 'secondary' : (u.role === 'RESEARCH_STAFF' ? 'success' : 'info'))} 
                    size="small" 
                    variant="outlined" 
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={u.is_active ? 'ใช้งานปกติ' : 'ปิดใช้งาน'} 
                    color={u.is_active ? 'success' : 'default'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                    <Tooltip title="แก้ไข">
                      <IconButton color="primary" size="small" onClick={() => handleOpen(u)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="ลบ">
                      <IconButton color="error" size="small" onClick={() => handleDelete(u.user_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>ไม่พบข้อมูลผู้ใช้งาน</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
            {editingId ? 'แก้ไขข้อมูลผู้ใช้งานระบบ' : 'เพิ่มผู้ใช้งานระบบใหม่'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
                <TextField
                  select
                  fullWidth
                  label="บทบาท (Role)"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="ADMIN">ADMIN (ผู้ดูแลระบบสูงสุด)</MenuItem>
                  <MenuItem value="DEAN">DEAN (ผู้บริหาร/คณบดี)</MenuItem>
                  <MenuItem value="STAFF">STAFF (เจ้าหน้าที่ฝ่ายฝึกฯ)</MenuItem>
                  <MenuItem value="RESEARCH_STAFF">RESEARCH_STAFF (เจ้าหน้าที่ฝ่ายวิจัย - เข้าถึงเฉพาะฐานข้อมูลวิจัย)</MenuItem>
                </TextField>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                helperText={editingId ? "เว้นว่างไว้หากไม่ต้องการเปลี่ยนรหัสผ่าน" : "รหัสผ่านเริ่มต้นคือ tpms1234"}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="อีเมล (สำหรับ Google Login)"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}><Chip label="ข้อมูลรายละเอียด" size="small" /></Divider>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                label="คำนำหน้า"
                name="prefix"
                value={formData.prefix}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                label="ชื่อ"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                error={!formData.first_name && open}
              />
            </Grid>
            <Grid size={{ xs: 4 }}>
              <TextField
                fullWidth
                label="นามสกุล"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                error={!formData.last_name && open}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="เบอร์โทรศัพท์"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={<Switch checked={formData.is_active === 1} onChange={handleChange} name="is_active" />}
                  label="สถานะการใช้งาน (Active)"
                />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            disabled={submitting}
          >
            {submitting ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemUserManager;
